// orderSummaryExport.js
//
// Builder for the Distributor Orders → Summary tab's "Export" button.
// Produces a single-sheet xlsx mirroring what's on screen right now:
//   - Same period mode (Monthly or Quarterly)
//   - Same region filter
//   - Same Actual / Goal / % to Goal sub-columns per period
//
// Ported from the source app's forecasting module. The only adaptation:
// `file-saver` isn't a Brand Command dependency, so the workbook is saved via
// a plain anchor-download (matches sales-forecasting's exportForecast.js).


// Dependency-free blob download (no file-saver).
function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const COLOR_NAVY      = "0A1628";
const COLOR_HEADER_BG = "1E3A5F";
const COLOR_AMBER     = "F5A623";
const COLOR_ALT_ROW   = "F0F4FA";
const COLOR_WHITE     = "FFFFFFFF";
const COLOR_MUTED     = "6B7280";
const COLOR_NAVYTEXT  = "FF0A1628";

const HEADER_FILL  = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + COLOR_HEADER_BG } };
const SUBHDR_FILL  = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2D4F7C" } };
const ALT_FILL     = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + COLOR_ALT_ROW } };
const WHITE_FILL   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
const GROUP_FILL   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFBE9C0" } };
const SUBTOTAL_FILL= { type: "pattern", pattern: "solid", fgColor: { argb: "FFFBE9C0" } };
const GRAND_FILL   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF4D67E" } };

const THIN_BORDER  = { style: "thin", color: { argb: "FFD1D5DB" } };
const HAIR_BORDER  = { style: "hair", color: { argb: "FFE2E8F0" } };

function pctToGoal(actual, goal) {
  if (!goal || goal <= 0) return null;
  return (Number(actual) / Number(goal)) * 100;
}

function pctColor(pct) {
  if (pct == null) return COLOR_MUTED;
  if (pct >= 100)  return "22863A";
  if (pct >= 75)   return "B7791F";
  return "B91C1C";
}

/**
 * @param opts {
 *   periodMode:  "month" | "quarter",
 *   periods:     [{ id, label, members, fullMembers, isQuarter, quarter?, year? }, ...],
 *   regions:     string[] — display regions (post-region-filter),
 *   sizeGroups:  [{ id: "187ml"|"1L"|"all_cases", label, subtotalLabel }, ...],
 *   getActual:   (groupId, region, period) => number,
 *   getGoal:     (groupId, region, period) => number,
 *   subtotalActual: (groupId, period) => number,
 *   subtotalGoal:   (groupId, period) => number,
 *   filterSummary: { year, startMonth, numMonths, region, periodMode },
 * }
 */
export async function exportSummaryToExcel(opts) {
  const {
    periodMode, periods, regions, sizeGroups,
    getActual, getGoal, subtotalActual, subtotalGoal,
    filterSummary,
  } = opts;

  // Loaded on demand — ExcelJS is ~400kB and only an export needs it.
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator  = "Brand Command";
  wb.created  = new Date();
  const ws    = wb.addWorksheet(periodMode === "quarter" ? "Quarterly Summary" : "Monthly Summary");

  const totalCols = 1 + periods.length * 3;
  ws.columns = [
    { width: 28 },
    ...periods.flatMap(() => [{ width: 12 }, { width: 12 }, { width: 10 }]),
  ];

  // ── Title block ───────────────────────────────────────────────────────
  const titleRow = ws.addRow(["Distributor Orders — Sales Summary"]);
  titleRow.height = 26;
  titleRow.getCell(1).font      = { bold: true, size: 16, color: { argb: COLOR_NAVYTEXT } };
  titleRow.getCell(1).alignment = { vertical: "middle" };
  if (totalCols > 1) ws.mergeCells(titleRow.number, 1, titleRow.number, totalCols);

  const asOf  = new Date();
  const dateStr = asOf.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
  const dateRow = ws.addRow([`As of ${dateStr}`]);
  dateRow.height = 16;
  dateRow.getCell(1).font = { italic: true, size: 10, color: { argb: "FF" + COLOR_MUTED } };
  if (totalCols > 1) ws.mergeCells(dateRow.number, 1, dateRow.number, totalCols);

  const fs       = filterSummary || {};
  const summary  = [
    `View: ${periodMode === "quarter" ? "Quarterly" : "Monthly"}`,
    fs.year != null ? `Year: ${fs.year}` : null,
    fs.startMonth ? `Start: ${fs.startMonth}` : null,
    fs.numMonths != null ? `Months: ${fs.numMonths}` : null,
    `Region: ${fs.region || "All Regions"}`,
  ].filter(Boolean).join("  ·  ");
  const sumRow = ws.addRow([summary]);
  sumRow.height = 16;
  sumRow.getCell(1).font = { size: 10.5, color: { argb: "FF" + COLOR_HEADER_BG } };
  if (totalCols > 1) ws.mergeCells(sumRow.number, 1, sumRow.number, totalCols);

  ws.addRow([]); // spacer

  // ── Header rows (period + Actual/Goal/%) ──────────────────────────────
  const periodRow = ws.addRow([
    "",
    ...periods.flatMap(p => [p.label, "", ""]),
  ]);
  periodRow.height = 22;
  periodRow.getCell(1).fill   = HEADER_FILL;
  periodRow.getCell(1).border = { bottom: { style: "medium", color: { argb: "FF" + COLOR_AMBER } } };
  periods.forEach((_, i) => {
    const startCol = 2 + i * 3;
    ws.mergeCells(periodRow.number, startCol, periodRow.number, startCol + 2);
    const cell = periodRow.getCell(startCol);
    cell.fill      = HEADER_FILL;
    cell.font      = { bold: true, size: 11, color: { argb: COLOR_WHITE } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border    = { bottom: { style: "medium", color: { argb: "FF" + COLOR_AMBER } }, right: THIN_BORDER };
  });

  const subRow = ws.addRow([
    "",
    ...periods.flatMap(() => ["Actual", "Goal", "% to Goal"]),
  ]);
  subRow.height = 18;
  subRow.getCell(1).fill   = SUBHDR_FILL;
  for (let c = 2; c <= totalCols; c++) {
    const cell  = subRow.getCell(c);
    const isPct = (c - 2) % 3 === 2;
    const isGoal = (c - 2) % 3 === 1;
    cell.fill      = SUBHDR_FILL;
    cell.font      = {
      bold: true, size: 9,
      color: { argb: isGoal ? "FF" + COLOR_AMBER : isPct ? "FF60A5FA" : "FFCBD5E1" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border    = { right: THIN_BORDER, bottom: THIN_BORDER };
  }

  ws.views = [{ state: "frozen", ySplit: subRow.number }];

  // ── Body — one section per size group ─────────────────────────────────
  let altToggle = false;
  for (const group of sizeGroups) {
    const sectionLabel = group.label === "All" ? "All Cases" : `${group.label} bottles`;
    const sectionRow   = ws.addRow([sectionLabel]);
    sectionRow.height  = 18;
    sectionRow.getCell(1).font      = { bold: true, size: 10, color: { argb: "FF" + COLOR_HEADER_BG } };
    sectionRow.getCell(1).fill      = GROUP_FILL;
    sectionRow.getCell(1).alignment = { vertical: "middle", indent: 1 };
    for (let c = 2; c <= totalCols; c++) {
      sectionRow.getCell(c).fill = GROUP_FILL;
      sectionRow.getCell(c).border = { right: THIN_BORDER, bottom: HAIR_BORDER };
    }
    ws.mergeCells(sectionRow.number, 1, sectionRow.number, totalCols);
    sectionRow.getCell(1).border = { bottom: HAIR_BORDER };

    for (const region of regions) {
      altToggle = !altToggle;
      const rowVals = [region];
      for (const p of periods) {
        const a = getActual(group.id, region, p);
        const g = getGoal(group.id, region, p);
        rowVals.push(a || null, g || null, null);
      }
      const row = ws.addRow(rowVals);
      row.height = 18;
      row.getCell(1).font      = { size: 10, color: { argb: COLOR_NAVYTEXT } };
      row.getCell(1).fill      = altToggle ? ALT_FILL : WHITE_FILL;
      row.getCell(1).alignment = { vertical: "middle", indent: 1 };
      row.getCell(1).border    = { right: THIN_BORDER, bottom: HAIR_BORDER };
      periods.forEach((p, i) => {
        const baseCol = 2 + i * 3;
        const a = getActual(group.id, region, p);
        const g = getGoal(group.id, region, p);
        const pct = pctToGoal(a, g);
        const actCell = row.getCell(baseCol);
        const goalCell = row.getCell(baseCol + 1);
        const pctCell  = row.getCell(baseCol + 2);
        for (const c of [actCell, goalCell, pctCell]) {
          c.fill      = altToggle ? ALT_FILL : WHITE_FILL;
          c.alignment = { horizontal: "right", vertical: "middle" };
          c.border    = { right: THIN_BORDER, bottom: HAIR_BORDER };
        }
        actCell.value     = a || null;
        actCell.numFmt    = "#,##0";
        actCell.font      = { size: 10, color: { argb: COLOR_NAVYTEXT } };
        goalCell.value    = g || null;
        goalCell.numFmt   = "#,##0";
        goalCell.font     = { size: 10, color: { argb: "FF" + COLOR_AMBER } };
        if (pct != null) {
          pctCell.value   = pct / 100;
          pctCell.numFmt  = "0%";
          pctCell.font    = { size: 10, bold: true, color: { argb: "FF" + pctColor(pct) } };
        } else {
          pctCell.value   = null;
          pctCell.font    = { size: 10, color: { argb: "FF" + COLOR_MUTED } };
        }
      });
    }

    const isGrand   = group.id === "all_cases";
    const totalRow  = ws.addRow([group.subtotalLabel]);
    totalRow.height = 20;
    totalRow.getCell(1).font      = { bold: true, size: 10, color: { argb: "FF" + COLOR_HEADER_BG } };
    totalRow.getCell(1).fill      = isGrand ? GRAND_FILL : SUBTOTAL_FILL;
    totalRow.getCell(1).alignment = { vertical: "middle", indent: 1 };
    totalRow.getCell(1).border    = {
      top:    { style: "medium", color: { argb: "FF" + COLOR_AMBER } },
      bottom: { style: isGrand ? "medium" : "thin", color: { argb: "FF" + COLOR_AMBER } },
      right:  THIN_BORDER,
    };
    periods.forEach((p, i) => {
      const baseCol = 2 + i * 3;
      const a = subtotalActual(group.id, p);
      const g = subtotalGoal(group.id, p);
      const pct = pctToGoal(a, g);
      const fillToUse = isGrand ? GRAND_FILL : SUBTOTAL_FILL;
      const fontColor = "FF" + COLOR_NAVY;
      const actCell  = totalRow.getCell(baseCol);
      const goalCell = totalRow.getCell(baseCol + 1);
      const pctCell  = totalRow.getCell(baseCol + 2);
      for (const c of [actCell, goalCell, pctCell]) {
        c.fill      = fillToUse;
        c.alignment = { horizontal: "right", vertical: "middle" };
        c.border    = {
          top:    { style: "medium", color: { argb: "FF" + COLOR_AMBER } },
          bottom: { style: isGrand ? "medium" : "thin", color: { argb: "FF" + COLOR_AMBER } },
          right:  THIN_BORDER,
        };
      }
      actCell.value   = a || null;
      actCell.numFmt  = "#,##0";
      actCell.font    = { bold: true, size: 10.5, color: { argb: fontColor } };
      goalCell.value  = g || null;
      goalCell.numFmt = "#,##0";
      goalCell.font   = { bold: true, size: 10.5, color: { argb: "FF" + COLOR_AMBER } };
      if (pct != null) {
        pctCell.value  = pct / 100;
        pctCell.numFmt = "0%";
        pctCell.font   = { bold: true, size: 10.5, color: { argb: "FF" + pctColor(pct) } };
      } else {
        pctCell.value  = null;
        pctCell.font   = { bold: true, size: 10.5, color: { argb: "FF" + COLOR_MUTED } };
      }
    });
  }

  const fnSuffix = periodMode === "quarter" ? "Quarterly" : "Monthly";
  const yearTag  = filterSummary?.year ?? new Date().getFullYear();
  const buf      = await wb.xlsx.writeBuffer();
  saveBlob(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `Distributor_Orders_Summary_${yearTag}_${fnSuffix}.xlsx`
  );
}
